'use client'

import * as React from 'react'
// --- Lib ---
import { getElementOverflowPosition } from '@/tiptap/lib/tiptap-collab-utils'
import { Input } from '@deepguide-ai/ui/input'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
// --- UI Primitives ---
import { Button, ButtonGroup } from '@/tiptap/tiptap-ui-primitive/button'
import { Card, CardBody, CardHeader } from '@/tiptap/tiptap-ui-primitive/card'
// --- Styles ---
import '@/tiptap/tiptap-ui/emoji-menu/emoji-menu.scss'
import type { EmojiItem } from '@tiptap/extension-emoji'
// --- Hooks ---
import { useMenuNavigation } from '@/hooks/use-menu-navigation'
import { getDisplayEmoji, getFilteredEmojis } from './emoji-menu-utils'

export interface EmojiMenuItemProps<T extends EmojiItem> {
  emoji: T
  index: number
  isSelected: boolean
  onSelect: (emoji: T) => void
  selector: string
}

export const EmojiMenuItem = <T extends EmojiItem>(
  props: EmojiMenuItemProps<T>
) => {
  const { emoji, isSelected, onSelect, selector } = props
  const itemRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    const menuElement = document.querySelector(selector) as HTMLElement
    if (!itemRef.current || !isSelected || !menuElement) return

    const overflow = getElementOverflowPosition(itemRef.current, menuElement)
    if (overflow === 'top') {
      itemRef.current.scrollIntoView(true)
    } else if (overflow === 'bottom') {
      itemRef.current.scrollIntoView(false)
    }
  }, [isSelected, selector])

  if (!emoji) return null

  return (
    <Button
      ref={itemRef}
      data-style='ghost'
      data-active-state={isSelected ? 'on' : 'off'}
      onClick={() => onSelect(emoji)}
      className='emoji-menu-item w-full min-w-0'
    >
      <span className='tiptap-button-emoji'>{getDisplayEmoji(emoji)}</span>
      <span className='tiptap-button-text'>:{emoji.name}:</span>
    </Button>
  )
}

export interface EmojiMenuProps<T extends EmojiItem> {
  emojis: T[]
  onSelect: (emoji: T) => void
  onClose?: () => void
  showSearch?: boolean
  selector?: string
}

export const EmojiMenu = <T extends EmojiItem>(props: EmojiMenuProps<T>) => {
  const {
    emojis,
    onSelect,
    onClose,
    showSearch = false,
    selector = '.emoji-menu-list [data-slot="scroll-area-viewport"]',
  } = props

  const containerRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  const [searchQuery, setSearchQuery] = React.useState('')

  const filteredEmojis = React.useMemo(() => {
    const filtered = getFilteredEmojis({ query: searchQuery, emojis })
    return filtered
  }, [emojis, searchQuery])

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    query: searchQuery,
    items: filteredEmojis,
    onSelect,
    onClose,
  })

  const renderedItems = React.useMemo(() => {
    return filteredEmojis.map((emoji, index) => (
      <EmojiMenuItem
        key={emoji.name}
        emoji={emoji}
        index={index}
        isSelected={index === selectedIndex}
        onSelect={() => onSelect(emoji)}
        selector={selector}
      />
    ))
  }, [filteredEmojis, selectedIndex, onSelect, selector])

  return (
    <Card ref={containerRef} >
      {showSearch && (
        <CardHeader>
          <Input
            ref={searchRef}
            type='text'
            placeholder='Add a emoji reaction...'
            value={searchQuery}
            autoFocus
            onChange={(e) => setSearchQuery(e.target.value)}
            className='h-8 text-[12px]'
          />
        </CardHeader>
      )}

      {renderedItems.length > 0 && (
        <CardBody className='w-full !overflow-hidden !p-0'>
          <ScrollArea
            type='hover'
            className='emoji-menu-list h-full p-1.5'
            viewClassName='rounded-none [&>div]:!block'
          >
            <ButtonGroup className='w-full min-w-0'>
              {renderedItems}
            </ButtonGroup>
          </ScrollArea>
        </CardBody>
      )}
    </Card>
  )
}
